import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { PageCanvas, SplitLayout } from '@/components/layout';
import { useDebounce } from '@/hooks/use-debounce';
import { keepPreviousDataWhen } from '@/hooks/use-query-loading-state';
import {
  getGetProductsProductIdQueryOptions,
  getGetProductsQueryKey,
  getGetProductsProductIdQueryKey,
  useGetProducts,
  useGetProductsProductId,
} from '@/api/generated/products/products';
import type {
  GetProducts200DataItem,
  GetProductsSortBy,
  GetProductsSortDirection,
} from '@/api/generated/schemas';
import { ProductDetail } from '@/components/products/product-detail';
import { ProductDetailSkeleton } from '@/components/products/product-detail-skeleton';
import { ProductEmptyPanel } from '@/components/products/product-empty-panel';
import { downloadProductsCsv } from '@/components/products/product-export';
import { ProductForm } from '@/components/products/product-form';
import { ProductList } from '@/components/products/product-list';
import { ProductManagementModals } from '@/components/products/product-management-modals';
import { ProductsOptionsSheet } from '@/components/products/products-options-sheet';
import { useProductPageActions } from '@/components/products/use-product-page-actions';
import { useCommercePermissionAccess } from '@/hooks/use-commerce-permission-access';
import { useClientPermissionAccess } from '@/hooks/use-client-permission-access';
import { formatDocumentTitle } from '@/lib/document-title';

export const Route = createFileRoute('/_auth/$orgId/products')({
  component: ProductsPage,
});

type PanelMode = 'empty' | 'detail' | 'new' | 'edit';

function SidePanelCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full bg-clovie-canvas p-3 lg:p-0">
      <div className="h-full overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border bg-clovie-surface shadow-soft-lg lg:rounded-none lg:border-0 lg:shadow-none">
        {children}
      </div>
    </div>
  );
}

function ProductsPage() {
  const { canManageProducts } = useCommercePermissionAccess();
  const { scopeKey } = useClientPermissionAccess();
  return canManageProducts ? <AuthorizedProductsPage key={scopeKey} /> : <p className="p-6 text-clovie-muted">You do not have access to product management.</p>;
}

function AuthorizedProductsPage() {
  const queryClient = useQueryClient();
  const { scopeKey } = useClientPermissionAccess();
  const canWriteOperational = useCommercePermissionAccess().canManageProducts;
  const [panelMode, setPanelMode] = useState<PanelMode>('empty');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<GetProductsSortBy>('category');
  const sortDirection: GetProductsSortDirection = 'asc';
  const [optionsOpen, setOptionsOpen] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  const productParams = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      sortBy,
      sortDirection,
    }),
    [debouncedSearch, sortBy, sortDirection]
  );

  const {
    data: listResponse,
    isLoading: isListLoading,
    isPlaceholderData: isListRefining,
    error: listError,
  } = useGetProducts(productParams, {
    query: {
      queryKey: [...getGetProductsQueryKey(productParams), scopeKey],
      // Typing a search / changing sort keeps the current rows (dimmed) — never across a salon/privacy scope.
      placeholderData: keepPreviousDataWhen((previousKey) => previousKey.at(-1) === scopeKey),
    },
  });
  const products = useMemo(() => listResponse?.data ?? [], [listResponse?.data]);

  const {
    data: detailResponse,
    error: detailError,
    isLoading: isDetailLoading,
  } = useGetProductsProductId(selectedProductId ?? '', {
    query: { enabled: !!selectedProductId, staleTime: 60_000, queryKey: [...getGetProductsProductIdQueryKey(selectedProductId ?? ''), scopeKey] },
  });
  const selectedProduct = detailResponse?.data ?? null;

  const actions = useProductPageActions({
    selectedProductId,
    setSelectedProductId,
    setPanelMode,
  });

  useEffect(() => {
    if (canWriteOperational || (panelMode !== 'new' && panelMode !== 'edit')) return;
    setPanelMode(selectedProductId ? 'detail' : 'empty');
  }, [canWriteOperational, panelMode, selectedProductId]);

  const handleRowHover = useCallback(
    (product: GetProducts200DataItem) => {
      queryClient.prefetchQuery(
        getGetProductsProductIdQueryOptions(product.id, { query: { staleTime: 60_000, queryKey: [...getGetProductsProductIdQueryKey(product.id), scopeKey] } })
      );
    },
    [queryClient, scopeKey]
  );

  const handleAdd = useCallback(() => {
    if (!canWriteOperational) return;
    setSelectedProductId(null);
    setPanelMode('new');
  }, [canWriteOperational]);

  const handleRowClick = useCallback((product: GetProducts200DataItem) => {
    setSelectedProductId(product.id);
    setPanelMode('detail');
  }, []);

  // Phones/tablets (single-pane): Back returns to the list.
  const handleMobileBack = useCallback(() => {
    setSelectedProductId(null);
    setPanelMode('empty');
  }, []);

  const handleDownloadProducts = useCallback(() => {
    try {
      downloadProductsCsv(products);
      toast.success('Product export downloaded');
    } catch {
      toast.error('Failed to download product export');
    }
  }, [products]);

  let sidePanel: React.ReactNode;
  if (panelMode === 'detail' && selectedProduct) {
    sidePanel = (
      <ProductDetail
        product={selectedProduct}
        onEdit={canWriteOperational ? () => setPanelMode('edit') : undefined}
        onDelete={canWriteOperational ? actions.openDeleteProduct : undefined}
        deleteDisabled={actions.isDeletingProduct}
        onInventoryChange={
          canWriteOperational
            ? (type, payload) => actions.addInventoryChange(selectedProduct.id, type, payload)
            : undefined
        }
        inventoryChangeInFlight={actions.isUpdatingInventoryChange}
        inventoryChangeError={actions.inventoryChangeError}
      />
    );
  } else if (canWriteOperational && panelMode === 'edit' && selectedProduct) {
    sidePanel = (
      <ProductForm
        mode="edit"
        product={selectedProduct}
        categories={actions.categories}
        categoriesLoading={actions.categoriesLoading}
        categoriesError={actions.categoriesError}
        isSubmitting={actions.isUpdatingProduct}
        onCancel={() => setPanelMode('detail')}
        onCreate={() => undefined}
        onUpdate={(data) => actions.updateProduct(selectedProduct.id, data)}
        onManageCategories={() => actions.setManageCategoriesOpen(true)}
        onDelete={actions.openDeleteProduct}
      />
    );
  } else if (panelMode === 'detail' && detailError) {
    sidePanel = <ProductEmptyPanel mode="error" />;
  } else if (panelMode === 'detail' && selectedProductId && isDetailLoading) {
    sidePanel = <ProductDetailSkeleton />;
  } else if (canWriteOperational && panelMode === 'new') {
    sidePanel = (
      <ProductForm
        mode="create"
        categories={actions.categories}
        categoriesLoading={actions.categoriesLoading}
        categoriesError={actions.categoriesError}
        isSubmitting={actions.isCreatingProduct}
        onCancel={() => setPanelMode(selectedProductId ? 'detail' : 'empty')}
        onCreate={actions.createProduct}
        onUpdate={() => undefined}
        onManageCategories={() => actions.setManageCategoriesOpen(true)}
      />
    );
  } else if (panelMode === 'edit' && selectedProductId && isDetailLoading) {
    sidePanel = <ProductDetailSkeleton />;
  } else {
    sidePanel = <ProductEmptyPanel />;
  }

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Products')}</title>
      </Helmet>

      <SplitLayout
        sidePanel={<SidePanelCard>{sidePanel}</SidePanelCard>}
        sidePanelWidth="md"
        mobileDetailOpen={panelMode !== 'empty'}
        onMobileBack={handleMobileBack}
        mobileBackLabel="Products"
      >
        <PageCanvas className="h-full overflow-auto">
          <div className="min-h-full overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface shadow-soft-lg">
            <ProductList
              products={products}
              selectedProductId={selectedProductId}
              search={search}
              onSearchChange={setSearch}
              onAdd={handleAdd}
              readOnly={!canWriteOperational}
              onOptions={() => setOptionsOpen(true)}
              onRowClick={handleRowClick}
              onRowHover={handleRowHover}
              isLoading={isListLoading}
              isRefining={isListRefining || search !== debouncedSearch}
              error={listError}
            />
          </div>
        </PageCanvas>
      </SplitLayout>

      {/* Outside SplitLayout: on phones the list pane is invisible while a product is open, and
          these modals do not portal — they must stay visible over the detail. */}
      <ProductsOptionsSheet
        open={optionsOpen}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        onManageCategories={
          canWriteOperational ? () => actions.setManageCategoriesOpen(true) : undefined
        }
        onDownloadProducts={handleDownloadProducts}
        onClose={() => setOptionsOpen(false)}
      />

      {canWriteOperational && (
        <ProductManagementModals actions={actions} selectedProduct={selectedProduct} />
      )}
    </>
  );
}
